import { apiClient, redirectToLogin } from "../services/httpClient";
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Link,
  MenuItem
} from '@mui/material';
import { ExpandMore, FilterAltOff } from '@mui/icons-material';
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { useNavigation } from "../contexts/RouterContext";
import { FaqButton } from "../components/FaqButton";
import StandardPagination from "../components/Pagination/StandardPagination";
import ResultCount from "../components/ResultCount";
import ClearFiltersButton from '../components/ClearFiltersButton';
import { colors, spacing, typography, inputs } from "../theme/designSystem";

interface MenuItemProps {
  label: string;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
}

interface UserSession {
  email: string;
  alias: string;
  clinicName: string;
  role: string;
  permissions: string[];
  loginTime: string;
}

interface FaqItem {
  id: string;
  category: string;
  question: string;
  answer: string;
}

interface PageFilter {
  title: string;
  faqIds: string[];
}

const Faq: React.FC = () => {
  const [userSession, setUserSession] = useState<UserSession | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  // Padrão FaqButton: chega já filtrado pelos FAQs da funcionalidade de origem (?from=<internal_page>)
  const [pageFilter, setPageFilter] = useState<PageFilter | null>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const { goToDashboard } = useNavigation();

  const [faqData, setFaqData] = useState<FaqItem[]>([]);
  const [loadingFaqs, setLoadingFaqs] = useState(false);

  // Manual do usuário: FAQs da base central, agrupados por feature (faq_type)
  useEffect(() => {
    const loadFaqs = async () => {
      setLoadingFaqs(true);
      try {
        const response = await apiClient.get("/faqs", {
          params: { PageNumber: 1, PageSize: 500 },
        });
        const items = Array.isArray(response.data?.items) ? response.data.items : [];
        setFaqData(
          items
            .filter((f: { isActive: boolean }) => f.isActive)
            .map((f: { id: string; question: string; answer: string; faqType: string }) => ({
              id: f.id,
              category: f.faqType || "Geral",
              question: f.question,
              answer: f.answer,
            }))
        );
      } catch (error) {
        console.error("Erro ao carregar FAQs:", error);
      } finally {
        setLoadingFaqs(false);
      }
    };
    loadFaqs();
  }, []);

  // Vínculo funcionalidade -> FAQs (functionalities.faq_list): sem vínculo, abre o FAQ completo
  useEffect(() => {
    const fromPage = new URLSearchParams(window.location.search).get("from");
    if (!fromPage) return;
    const loadPageFilter = async () => {
      try {
        const response = await apiClient.get(`/faqs/page/${encodeURIComponent(fromPage)}`);
        const faqIds = Array.isArray(response.data?.faqIds) ? response.data.faqIds : [];
        if (faqIds.length > 0) {
          setPageFilter({ title: response.data.functionalityTitle || fromPage, faqIds });
        }
      } catch (error) {
        console.error("Erro ao carregar FAQs da página de origem:", error);
      }
    };
    loadPageFilter();
  }, []);

  const categories = ["all", ...Array.from(new Set(faqData.map(item => item.category))).sort((a, b) => a.localeCompare(b, "pt-BR"))];

  useEffect(() => {
    const sessionData = localStorage.getItem('clinic4us-user-session');
    if (sessionData) {
      try {
        const session = JSON.parse(sessionData);
        setUserSession(session);
      } catch (error) {
        console.error('Erro ao carregar sessão:', error);
        redirectToLogin();
      }
    } else {
      redirectToLogin();
    }
  }, []);

  const handleRevalidateLogin = () => {
    console.log("Revalidar login");
  };

  const handleNotificationClick = () => {
    console.log("Notificações clicadas");
  };

  const handleUserClick = () => {
    console.log("Usuário clicado");
  };

  const handleLogoClick = () => {
    goToDashboard();
  };

  const toggleItem = (id: string) => {
    setExpandedItems(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const filteredFaq = faqData.filter(item => {
    const matchesSearch = item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || item.category === selectedCategory;
    const matchesPage = !pageFilter || pageFilter.faqIds.includes(item.id);
    return matchesSearch && matchesCategory && matchesPage;
  }).sort((a, b) => a.category.localeCompare(b.category, "pt-BR") || a.question.localeCompare(b.question, "pt-BR"));

  // Paginação
  const totalPages = Math.max(1, Math.ceil(filteredFaq.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredFaq.slice(startIndex, endIndex);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedCategory("all");
    setPageFilter(null);
  };

  const hasFilters = searchTerm !== "" || selectedCategory !== "all" || pageFilter !== null;

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, pageFilter]);

  if (!userSession) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={userSession.email}
        userProfile={userSession.role}
        clinicName={userSession.clinicName}
        notificationCount={5}
        onRevalidateLogin={handleRevalidateLogin}
        onNotificationClick={handleNotificationClick}
        onUserClick={handleUserClick}
        onLogoClick={handleLogoClick}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          {/* Título da Página */}
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                FAQ - Perguntas Frequentes
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Encontre respostas para as dúvidas mais comuns sobre o sistema.
              </Typography>
            </Box>
            <FaqButton />
          </Box>

          {/* Filtros, Paginação e Lista de FAQ */}
          <Paper elevation={0} className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
                <TextField
                  select
                  label="Categoria"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    MenuProps: {
                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                      transformOrigin: { vertical: 'top', horizontal: 'left' },
                      PaperProps: { sx: { maxHeight: 360 } },
                    },
                  }}
                  sx={{
                    minWidth: '200px',
                    flex: '1 1 200px',
                    '& .MuiOutlinedInput-root': {
                      height: inputs.default.height,
                      '& fieldset': {
                        borderColor: colors.border,
                      },
                      '&:hover fieldset': {
                        borderColor: colors.border,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: colors.primary,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: inputs.default.labelFontSize,
                      color: colors.textSecondary,
                      backgroundColor: colors.white,
                      padding: inputs.default.labelPadding,
                      '&.Mui-focused': {
                        color: colors.primary,
                      },
                    },
                  }}
                >
                  <MenuItem value="all">Todas as categorias</MenuItem>
                  {categories.filter(cat => cat !== "all").map(category => (
                    <MenuItem key={category} value={category}>{category}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Busca por palavra"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Pesquisar FAQ"
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    flex: '2 1 300px',
                    '& .MuiOutlinedInput-root': {
                      height: inputs.default.height,
                      '& fieldset': {
                        borderColor: colors.border,
                      },
                      '&:hover fieldset': {
                        borderColor: colors.border,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: colors.primary,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: inputs.default.labelFontSize,
                      color: colors.textSecondary,
                      backgroundColor: colors.white,
                      padding: inputs.default.labelPadding,
                      '&.Mui-focused': {
                        color: colors.primary,
                      },
                    },
                  }}
                />

              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={handleClearFilters} disabled={!hasFilters} />
              </div>
            </div>

            {/* Filtro pela funcionalidade de origem (botão FAQ da página) */}
            {pageFilter && (
              <Box sx={{ mt: -0.5, mb: 1.5 }}>
                <Chip
                  label={`FAQs de: ${pageFilter.title}`}
                  onDelete={() => setPageFilter(null)}
                  sx={{
                    backgroundColor: colors.primaryLight,
                    color: colors.primary,
                    fontSize: typography.fontSize.sm,
                    fontWeight: typography.fontWeight.medium,
                    '& .MuiChip-deleteIcon': {
                      color: colors.primary,
                    },
                  }}
                />
              </Box>
            )}

            <ResultCount shown={filteredFaq.length} total={faqData.length} />

            {/* Lista de FAQ */}
            <Box sx={{ mt: 2 }}>
            {currentItems.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography sx={{ color: colors.textSecondary }}>
                  Nenhum item encontrado para sua busca.
                </Typography>
              </Box>
            ) : (
              currentItems.map((item) => (
                <Accordion
                  key={item.id}
                  expanded={expandedItems.includes(item.id)}
                  onChange={() => toggleItem(item.id)}
                  elevation={0}
                  sx={{
                    mb: 1,
                    borderRadius: '8px !important',
                    border: `1px solid ${colors.backgroundAlt}`,
                    '&:before': {
                      display: 'none',
                    },
                    '&.Mui-expanded': {
                      margin: '0 0 8px 0',
                    },
                    '&:last-child': {
                      mb: 0,
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore sx={{ color: colors.primary }} />}
                    sx={{
                      '&.Mui-expanded': {
                        borderBottom: `1px solid ${colors.backgroundAlt}`,
                      },
                    }}
                  >
                    <Box>
                      <Chip
                        label={item.category}
                        size="small"
                        sx={{
                          mb: 1,
                          backgroundColor: colors.primaryLight,
                          color: colors.primary,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.medium,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: typography.fontSize.base,
                          fontWeight: typography.fontWeight.semibold,
                          color: colors.textPrimary,
                        }}
                      >
                        {item.question}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 2 }}>
                    <Typography
                      sx={{
                        fontSize: typography.fontSize.sm,
                        color: colors.textPrimary,
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.7,
                        mb: 2
                      }}
                    >
                      {item.answer}
                    </Typography>

                    {/* Vídeo */}

                    {/* Links */}

                  </AccordionDetails>
                </Accordion>
              ))
            )}
            </Box>

            {/* Paginação Inferior */}
            {currentItems.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <StandardPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredFaq.length}
                  onPageChange={(page) => {
                    setCurrentPage(page);
                    setTimeout(scrollToTop, 100);
                  }}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              </Box>
            )}
          </Paper>
        </Container>
      </Box>

      <FooterInternal />
    </Box>
  );
};

export default Faq;
